import React, {useCallback, useEffect, useState} from 'react'
import {
  FaExclamationCircle,
  FaFileAlt,
  FaCheckCircle,
  FaPaperclip,
  FaCommentDots,
  FaCloudUploadAlt,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
} from 'react-icons/fa'
import axios from 'axios'
import './../style.css'
import ChatModal from './ChatModal'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Pusher from 'pusher-js'
import {Spinner} from 'react-bootstrap'
import styled from 'styled-components'
import {Modal} from 'react-bootstrap'

interface DocumentsProps {
  claimDetails: any
  onDocumentClick: (docLabel: string) => void
  onDocumentsStatusChange: (status: boolean) => void
  onDocumentValidationChange?: (hasInvalid: boolean) => void
  onDocumentRealtimeUpdate?: (data: any) => void
}

interface ReuploadModalState {
  isOpen: boolean
  docKey: number | null
  fileIndex: number | null
  documentLabel: string
  position: {top: number; left: number} | null
}

const ProgressBar = styled.div<{$progress: number}>`
  width: ${(props) => props.$progress}%;
  height: 8px;
  background: linear-gradient(90deg, #059bb6 0%, #05b6a6 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
`

const Documents: React.FC<DocumentsProps> = ({
  claimDetails,
  onDocumentClick,
  onDocumentsStatusChange,
  onDocumentValidationChange,
  onDocumentRealtimeUpdate,
}) => {
  const user = useAuth()
  const {auth} = useAuth()
  console.log('Token Documents: ', user.currentUser?.token)
  const [documentFiles, setDocumentFiles] = useState<Record<string, any[]>>({})
  const [uploadStatus, setUploadStatus] = useState<Record<string, boolean | null>>({})
  const [documentsData, setDocumentsData] = useState<any[]>([])
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)
  const [chatMessage, setChatMessage] = useState<string>('')
  const [activeOptionsModal, setActiveOptionsModal] = useState<{
    key: number
    label: string
    mode?: 'replace' | 'delete'
    status?: 'IDLE' | 'VALID'
  } | null>(null)
  const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({})
  const [lastToastTime, setLastToastTime] = useState<number>(0)
  const [modalPosition, setModalPosition] = useState<{top: number; left: number} | null>(null)
  const [reuploadModal, setReuploadModal] = useState<ReuploadModalState>({
    isOpen: false,
    docKey: null,
    fileIndex: null,
    documentLabel: '',
    position: null,
  })
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({})
  const [uploadingLabel, setUploadingLabel] = useState<string>('')
  const [showOptionsModal, setShowOptionsModal] = useState(false)
  const [showReuploadModal, setShowReuploadModal] = useState(false)

  // For Daily Monitoring, we won't use default documents
  const isDailyMonitoring = claimDetails.apiEndpoint?.includes('area=Daily%20Monitoring')

  console.log('claimDetails Data: ', claimDetails)

  // Replace initializing with defaultDocuments - set empty object if Daily Monitoring
  useEffect(() => {
    const initialFiles: Record<string, any[]> = {}
    setDocumentFiles(initialFiles)
  }, [])

  const checkAllDocumentsUploaded = useCallback(() => {
    // Get all unique document labels from API response
    const allDocumentLabels = Object.keys(documentFiles)

    // For Daily Monitoring, we only check documents from API
    const allUploaded = allDocumentLabels.every((label) => {
      // Skip Prescription documents
      if (label === 'Prescription') return true

      const files = documentFiles[label]
      return files && files.length > 0 && !files.some((file) => file.status === 'INVALID')
    })

    onDocumentsStatusChange(allUploaded)
  }, [documentFiles, onDocumentsStatusChange])

  useEffect(() => {
    checkAllDocumentsUploaded()
  }, [documentFiles, checkAllDocumentsUploaded])

  const fetchDocuments = async () => {
    const claimNumber = claimDetails.claimDetails.claimNumber
    // Use the apiEndpoint from claimDetails if provided, otherwise use the default endpoint
    const apiUrl =
      claimDetails.apiEndpoint ||
      `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Daily Monitoring`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    try {
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        const apiDocuments = response.data.data.filter(
          (doc: any) => doc.label !== 'Prescription' && doc.label !== 'Chat Document'
        )

        // Check for invalid documents across all documents
        const hasInvalidDocuments = apiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some((file: any) => file.status === 'INVALID')
        )

        // For Daily Monitoring, we don't check against defaultDocuments
        const allDocumentsUploaded =
          apiDocuments.length > 0 &&
          apiDocuments.every((doc: any) => doc.document && doc.document.length > 0)

        // Use API documents directly without merging with defaultDocuments
        const finalDocuments = apiDocuments.map((doc: any, index: number) => ({
          label: doc.label,
          key: index,
          document: doc.document,
        }))

        // Create updated files object
        const updatedFiles: Record<string, any[]> = {}
        finalDocuments.forEach((doc: any) => {
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name ? file.name.split('.').pop() : '',
              status: file.status,
              isArchive: file.isArchive,
            }))
          } else {
            updatedFiles[doc.label] = []
          }
        })

        console.log('apiDocuments', apiDocuments)

        setDocumentsData(finalDocuments)
        setDocumentFiles(updatedFiles)

        // Update parent components
        const allDocumentsValid = !hasInvalidDocuments && allDocumentsUploaded
        onDocumentsStatusChange(allDocumentsValid)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(!allDocumentsValid)
        }

        // Check if all documents have IDLE status
        const allDocumentsIdle = apiDocuments.every(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.every(
              (file: any) => file.status === 'IDLE' || file.status === 'Request Document'
            )
        )

        // If all documents are uploaded and have IDLE status, enable the button
        if (allDocumentsUploaded && allDocumentsIdle) {
          onDocumentsStatusChange(true)
          if (onDocumentValidationChange) {
            onDocumentValidationChange(false)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to fetch documents. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [claimDetails])

  useEffect(() => {
    console.log('Token in useEffect:', user)
  }, [user])

  useEffect(() => {
    const pusher = new Pusher(`${process.env.REACT_APP_API_PUSHER_KEY}`, {
      cluster: process.env.REACT_APP_API_PUSHER_CLUSTER! || 'ap1',
      forceTLS: true,
    })

    const claimNumber = claimDetails.claimDetails.claimNumber
    const fileStatusChannel = pusher.subscribe(`file-channel-${claimNumber}`)
    const listFileChannel = pusher.subscribe(`list-file-channel-${claimNumber}`)

    fileStatusChannel.bind(`get-${claimNumber}`, (data: any) => {
      if (data && data.message) {
        const {message} = data
        const {Label, Status, Id, DocumentName, isArchive, UpdatedBy} = message

        // Update document files state immediately
        setDocumentFiles((prevFiles) => {
          const newFiles = {...prevFiles}
          if (newFiles[Label]) {
            newFiles[Label] = [
              {
                name: DocumentName,
                id: Id,
                type: DocumentName ? DocumentName.split('.').pop() : '',
                status: Status,
                isArchive: isArchive,
              },
            ]
          }
          return newFiles
        })

        // Update documents data state
        setDocumentsData((prevData) => {
          return prevData.map((doc) => {
            if (doc.label === Label) {
              return {
                ...doc,
                document: [
                  {
                    name: DocumentName,
                    id: Id,
                    status: Status,
                    isArchive: isArchive,
                  },
                ],
              }
            }
            return doc
          })
        })

        // Check for any invalid documents
        const hasInvalidDocuments = Object.values(documentFiles).some((files) =>
          files.some((file) => file.status === 'INVALID')
        )

        // Check if all documents are uploaded, for Daily Monitoring we don't use defaultDocuments
        const allDocumentsUploaded = Object.keys(documentFiles).length > 0

        // Update parent components
        onDocumentsStatusChange(allDocumentsUploaded && !hasInvalidDocuments)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(hasInvalidDocuments)
        }

        // Show toast for invalid documents
        const now = Date.now()
        if (Status === 'INVALID' && now - lastToastTime > 5000) {
          toast.error(`${Label} has been rejected by ${UpdatedBy}. Please review and resubmit.`, {
            position: 'top-right',
            autoClose: 3000,
          })
          setLastToastTime(now)
        }
      }
    })

    listFileChannel.bind(`get-list-${claimNumber}`, (data: any) => {
      console.log('Received list file data:', data)

      if (data.isSuccess) {
        // Pass the realtime data up to parent immediately
        if (onDocumentRealtimeUpdate) {
          onDocumentRealtimeUpdate(data)
        }

        const apiDocuments = data.data.filter(
          (doc: any) => doc.label !== 'Prescription' && doc.label !== 'Chat Document'
        )
        console.log('data', data)

        console.log('apiDocuments', apiDocuments)

        // Check for invalid documents
        const hasInvalidDocuments = apiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some((file: any) => file.status === 'INVALID')
        )

        // For Daily Monitoring, all documents from API are considered required
        const allDocumentsUploaded =
          apiDocuments.length > 0 &&
          apiDocuments.every((doc: any) => doc.document && doc.document.length > 0)

        // Check if all documents have IDLE or Request Document status
        const allDocumentsIdle = apiDocuments.every(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.every(
              (file: any) => file.status === 'IDLE' || file.status === 'Request Document'
            )
        )

        // Calculate final validation state
        const allDocumentsValid = allDocumentsUploaded && allDocumentsIdle && !hasInvalidDocuments

        // Always use API documents directly (no merging with defaultDocuments)
        let finalDocuments = apiDocuments.map((doc: any, index: number) => ({
          label: doc.label,
          key: index,
          document: doc.document,
        }))

        // Create updated files object
        const updatedFiles: Record<string, any[]> = {}

        finalDocuments.forEach((doc: any) => {
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name ? file.name.split('.').pop() : '',
              status: file.status,
              isArchive: file.isArchive,
            }))
          } else {
            updatedFiles[doc.label] = []
          }
        })

        // Notify parent components of status changes immediately
        onDocumentsStatusChange(allDocumentsValid)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(!allDocumentsValid)
        }

        // Update local state after notifying parent
        setDocumentsData(finalDocuments)
        setDocumentFiles(updatedFiles)
      }
    })

    return () => {
      fileStatusChannel.unbind_all()
      pusher.unsubscribe(`file-channel-${claimNumber}`)
      listFileChannel.unbind_all()
      pusher.unsubscribe(`list-file-channel-${claimNumber}`)
    }
  }, [claimDetails.claimDetails.claimNumber, documentFiles])

  const status = claimDetails?.claimDetails?.status

  // Modify the isInteractionDisabled function to include Reject and Void statuses
  const isInteractionDisabled = () => {
    const disabledStatuses = [
      'Request IGL',
      'IGL RELEASED',
      'Daily Monitoring',
      'Request FGL',
      'FGL RELEASED',
      'Reject',
      'Void',
      'IGL Released',
      'FGL Released',
    ]
    return !disabledStatuses.includes(status)
  }

  // Modify handleFileChange to check status first
  const handleFileChange = async (label: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (isInteractionDisabled()) return

    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files)

      try {
        const currentDoc = documentsData.find((doc) => doc.label === label)
        const fileInput = event.target as HTMLInputElement
        const currentFileId = fileInput.dataset.replaceFileId
          ? parseInt(fileInput.dataset.replaceFileId)
          : currentDoc?.document?.[0]?.id

        // Immediately update UI to show IDLE status
        setDocumentFiles((prevState) => ({
          ...prevState,
          [label]: [
            {
              name: selectedFiles[0].name || '',
              type: selectedFiles[0].name.split('.').pop() || '',
              status: 'IDLE',
              id: currentFileId || 0,
            },
          ],
        }))

        // Also update documentsData for immediate UI reflection
        setDocumentsData((prevData) =>
          prevData.map((doc) => {
            if (doc.label === label) {
              return {
                ...doc,
                document: [
                  {
                    name: selectedFiles[0].name,
                    id: currentFileId || 0,
                    status: 'IDLE',
                    isArchive: false,
                  },
                ],
              }
            }
            return doc
          })
        )

        // Attempt upload
        await uploadFiles(label, selectedFiles, currentFileId)

        // Clear the replaceFileId after upload
        if (fileInput.dataset.replaceFileId) {
          delete fileInput.dataset.replaceFileId
        }
      } catch (error) {
        console.error('Error handling file change:', error)
        toast.error('Failed to upload file. Please try again.', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      }
    }
  }

  const uploadFiles = async (label: string, files: File[], existingFileId?: number) => {
    if (!files || files.length === 0) return

    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    const claimNumber = claimDetails.claimDetails.claimNumber
    const typeDocument = label

    // Always use the existing file ID if available
    const documentId = existingFileId || 0
    console.log('Using Document ID for upload:', documentId)

    const apiUrl = `${
      process.env.REACT_APP_API_CLAIM_URL
    }/api/Claims/UploadFiles?claimNumber=${claimNumber}&typeDocument=${encodeURIComponent(
      typeDocument
    )}&user=${user.currentUser?.email}&bucket=Daily Monitoring&status=VALID&id=${documentId}`

    try {
      setUploadStatus((prevStatus) => ({...prevStatus, [label]: null}))
      setIsUploading((prev) => ({...prev, [label]: true}))
      setUploadingLabel(label)
      setUploadProgress(0)

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${user.currentUser?.token}`,
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const current = progressEvent.loaded
          const percentage = Math.round((current / total) * 100)
          setUploadProgress(percentage)
        },
      })

      if (response.data.isSuccess) {
        // After successful upload, fetch updated document list
        const listResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Daily Monitoring`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listResponse.data.isSuccess) {
          // Update local state
          await fetchDocuments()

          // Notify parent components immediately
          if (onDocumentRealtimeUpdate) {
            onDocumentRealtimeUpdate(listResponse.data)
          }

          const apiDocuments = listResponse.data.data.filter(
            (doc: any) => doc.label !== 'Prescription'
          )

          // Check document statuses
          const hasInvalidDocuments = apiDocuments.some(
            (doc: any) =>
              doc.document &&
              doc.document.length > 0 &&
              doc.document.some((file: any) => file.status === 'INVALID')
          )

          const allDocumentsUploaded =
            apiDocuments.length > 0 &&
            apiDocuments.every((doc: any) => doc.document && doc.document.length > 0)

          const allDocumentsIdle = apiDocuments.every(
            (doc: any) =>
              doc.document &&
              doc.document.length > 0 &&
              doc.document.every(
                (file: any) => file.status === 'IDLE' || file.status === 'Request Document'
              )
          )

          // Update parent components with the latest status
          const allDocumentsValid = allDocumentsUploaded && !hasInvalidDocuments && allDocumentsIdle
          onDocumentsStatusChange(allDocumentsValid)
          if (onDocumentValidationChange) {
            onDocumentValidationChange(!allDocumentsValid)
          }
        }

        setUploadStatus((prevStatus) => ({...prevStatus, [label]: true}))
        toast.success('File uploaded successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      } else {
        throw new Error(response.data.message || 'Upload failed')
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      setUploadStatus((prevStatus) => ({...prevStatus, [label]: false}))
      toast.error('Failed to upload file. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
      throw error
    } finally {
      setIsUploading((prev) => ({...prev, [label]: false}))
      setUploadProgress(0)
    }
  }

  console.log('Token Documents: ', user.currentUser?.token)

  const handleDeleteFile = async (docKey: number, fileIndex: number) => {
    try {
      const claimNumber = claimDetails.claimDetails.claimNumber
      const currentDoc = documentsData.find((doc) => doc.key === docKey)
      const fileToDelete = currentDoc?.document[fileIndex]

      if (!fileToDelete) {
        toast.error('File not found', {
          position: 'bottom-right',
          autoClose: 3000,
        })
        return
      }

      setDeletingFiles((prev) => ({...prev, [fileToDelete.id]: true}))

      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${fileToDelete.id}&claimNumber=${claimNumber}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      const response = await axios.post(apiUrl, {}, {headers})

      if (response.data === 'DELETED FILE SUCCESS.' || response.data.isSuccess) {
        // Update the documentFiles state
        setDocumentFiles((prev) => {
          const newFiles = {...prev}
          const docLabel = documentsData.find((doc) => doc.key === docKey)?.label
          if (docLabel) {
            newFiles[docLabel] = newFiles[docLabel].filter((file) => file.id !== fileToDelete.id)
          }
          return newFiles
        })

        setDocumentsData((prevData) =>
          prevData.map((doc) => {
            if (doc.key === docKey) {
              return {
                ...doc,
                document: doc.document.filter((_, index) => index !== fileIndex),
              }
            }
            return doc
          })
        )

        toast.success('File deleted successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        })

        await fetchDocuments()
        closeOptionsModal() // Close the modal after successful deletion
      } else {
        throw new Error('Delete operation failed')
      }
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Error deleting file. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    } finally {
      setDeletingFiles({})
    }
  }

  // Modify handleReUpload to include position
  // const handleReUpload = (docKey: number, fileIndex: number, event: React.MouseEvent) => {
  //   event.stopPropagation()
  //   const currentDoc = documentsData.find((doc) => doc.key === docKey)
  //   if (currentDoc) {
  //     setReuploadModal({
  //       isOpen: true,
  //       docKey,
  //       fileIndex,
  //       documentLabel: currentDoc.label,
  //       position: null,
  //     })
  //     setShowReuploadModal(true)
  //     closeOptionsModal() // Close the options modal
  //   }
  // }

  const handleReUpload = () => {
    if (activeOptionsModal) {
      console.log('Active Options Modal: ', activeOptionsModal)
      const fileInput = document.getElementById(
        `upload-${activeOptionsModal.label}`
      ) as HTMLInputElement
      if (fileInput) {
        // Find the current document and its file ID
        const currentDoc = documentsData.find((doc) => doc.label === activeOptionsModal.label)
        const currentFileId = currentDoc?.document?.[0]?.id

        // Store the file ID for use during upload
        if (currentFileId) {
          fileInput.dataset.replaceFileId = currentFileId.toString()
        }

        fileInput.click()
        closeOptionsModal()
      }
    }
  }

  // Add new function to handle actual reupload after confirmation
  const handleConfirmReupload = () => {
    if (reuploadModal.docKey !== null && reuploadModal.fileIndex !== null) {
      const currentDoc = documentsData.find((doc) => doc.key === reuploadModal.docKey)
      const currentFile = currentDoc?.document[reuploadModal.fileIndex]

      if (currentFile && currentDoc) {
        const fileInput = document.getElementById(`upload-${currentDoc.label}`) as HTMLInputElement
        if (fileInput) {
          fileInput.dataset.replaceFileId = currentFile.id.toString()
          fileInput.value = '' // Clear the input to allow re-uploading the same file
          fileInput.click()
        }
      }
    }
    setReuploadModal({
      isOpen: false,
      docKey: null,
      fileIndex: null,
      documentLabel: '',
      position: null,
    })
    setShowReuploadModal(false)
  }

  // Update closeReuploadModal
  const closeReuploadModal = () => {
    setReuploadModal({
      isOpen: false,
      docKey: null,
      fileIndex: null,
      documentLabel: '',
      position: null,
    })
    setShowReuploadModal(false)
  }

  // Modify openOptionsModal to use clientX and clientY
  const openOptionsModal = (
    doc: {label: string; key: number; status: 'IDLE' | 'VALID'},
    event: React.MouseEvent
  ) => {
    if (isInteractionDisabled()) return

    // Find the current document
    const currentDoc = documentsData.find((d) => d.label === doc.label)?.document?.[0]

    // Don't open modal if document status is VALID
    if (currentDoc?.status === 'VALID') return

    // Get the click coordinates
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()

    // Position the modal at the click position
    setModalPosition({
      top: rect.top,
      left: rect.right + 8,
    })

    setActiveOptionsModal({key: doc.key, label: doc.label, status: doc.status})
    setShowOptionsModal(true)
    event.stopPropagation()
  }

  const closeOptionsModal = () => {
    setActiveOptionsModal(null)
    setShowOptionsModal(false)
  }

  const handleSendToChat = () => {
    if (activeOptionsModal) {
      setChatMessage(`${activeOptionsModal.label}: Discuss document details`)
      setIsChatOpen(true)
      closeOptionsModal()
    }
  }

  const handleFileReplace = (docLabel: string, fileToReplace: any) => {
    const fileInput = document.getElementById(`upload-${docLabel}`) as HTMLInputElement
    if (fileInput) {
      if (fileToReplace?.id) {
        fileInput.dataset.replaceFileId = fileToReplace.id.toString()
      }
      fileInput.click()
    }
  }

  const getDocumentStatusIcon = (document: any) => {
    if (!document || !document.name) {
      return <FaExclamationCircle className='document-icon warning' />
    }

    switch (document.status) {
      case 'VALID':
      case 'Valid':
        return <FaCheckCircle className='document-icon success' />
      case 'REQUEST':
        return <FaExclamationTriangle className='document-icon request' />
      default:
        return <FaExclamationCircle className='document-icon warning' />
    }
  }

  const getDocumentStatusText = (document: any) => {
    if (!document || !document.name) {
      return 'Document not uploaded'
    }

    switch (document.status) {
      case 'VALID':
        return 'Approved'
      case 'INVALID':
        return 'Revision needed'
      default:
        return 'Uploaded'
    }
  }

  return (
    <>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        style={{zIndex: 9999}}
      />
      <div className='documents-container' style={{marginTop: '60px'}}>
        {documentsData.map((doc, index) => {
          const currentDoc = doc.document

          return (
            <div
              className={`document-item ${
                activeOptionsModal?.label === doc.label ? 'active' : ''
              } ${isInteractionDisabled() ? 'disabled' : ''}`}
              key={doc.label}
            >
              <div
                className={`document-header ${isInteractionDisabled() ? 'disabled' : ''}`}
                style={{
                  cursor:
                    isInteractionDisabled() || currentDoc?.[0]?.status === 'VALID'
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <div className='document-header-content'>
                  {getDocumentStatusIcon(currentDoc?.[0])}
                  <span className='document-text'>{doc.label}</span>
                  {currentDoc?.[0]?.status === 'REQUEST' && (
                    <span className='document-status request'>Revision needed</span>
                  )}
                  {currentDoc?.[0]?.status === 'Request Document' && (
                    <span className='document-status request'>Document Requested</span>
                  )}
                  {(!currentDoc || currentDoc.length === 0) && !isInteractionDisabled() && (
                    <button
                      className={`upload-button ${isInteractionDisabled() ? 'disabled' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        const fileInput = document.getElementById(`upload-${doc.label}`)
                        if (fileInput) fileInput.click()
                      }}
                      disabled={isInteractionDisabled()}
                    >
                      <FaPaperclip className='upload-icon' />
                      Upload Document
                    </button>
                  )}
                </div>
                {/* Reupload Button */}
                {currentDoc &&
                  currentDoc.length > 0 &&
                  !isInteractionDisabled() &&
                  currentDoc[0]?.status !== 'VALID' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        // Set the active document and show the modal
                        setActiveOptionsModal({key: doc.key, label: doc.label, status: doc.status})
                        setShowOptionsModal(true)
                      }}
                      style={{
                        width: '22%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: 'none',
                        background:
                          'linear-gradient(90deg, rgba(5, 155, 182, 0.08) 0%, rgba(5, 182, 166, 0.08) 100%)',
                        color: '#059BB6',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        marginLeft: 'auto',
                        marginRight: '8px',
                        transition: 'all 0.2s',
                      }}
                    >
                      <FaCloudUploadAlt style={{fontSize: '14px'}} />
                      {currentDoc[0]?.status === 'IDLE' ||
                      currentDoc[0]?.status === 'Request Document'
                        ? 'Upload'
                        : 'Re-upload'}
                    </button>
                  )}
              </div>

              {/* Add upload progress indicator */}
              {isUploading[doc.label] && (
                <div
                  style={{
                    padding: '12px',
                    background: 'white',
                    borderBottom: '1px solid #f1f3f5',
                  }}
                >
                  <div
                    style={{
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#059BB6',
                        fontWeight: 500,
                        fontSize: '13px',
                      }}
                    >
                      <Spinner
                        animation='border'
                        size='sm'
                        style={{
                          marginRight: '8px',
                          color: '#059BB6',
                          width: '16px',
                          height: '16px',
                        }}
                      />
                      Uploading...
                    </span>
                    <span
                      style={{
                        background: '#059BB6',
                        color: 'white',
                        borderRadius: '20px',
                        padding: '2px 10px',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}
                    >
                      {uploadProgress}%
                    </span>
                  </div>
                  <div
                    style={{
                      background: '#f0f0f0',
                      borderRadius: '6px',
                      height: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <ProgressBar $progress={uploadProgress} />
                  </div>
                </div>
              )}

              {currentDoc && currentDoc.length > 0 && (
                <div className='document-files-list'>
                  {currentDoc.map((file: any, fileIndex: number) => (
                    <div
                      className={`document-file-item ${isInteractionDisabled() ? 'disabled' : ''}`}
                      key={file.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!isInteractionDisabled() && file.status !== 'VALID') {
                          const rect = e.currentTarget.getBoundingClientRect()
                          setModalPosition({
                            top: rect.top,
                            left: rect.right + 8,
                          })
                          setActiveOptionsModal({
                            key: doc.key,
                            label: doc.label,
                            status: currentDoc[0]?.status,
                          })
                        }
                      }}
                    >
                      {file.type === 'pdf' ? (
                        <FaFileAlt className='file-icon pdf' />
                      ) : (
                        <FaPaperclip className='file-icon' />
                      )}
                      <span className='document-file-name'>{file.name}</span>
                      <span className={`status-badge ${file.status.toLowerCase()}`}>
                        {getDocumentStatusText(file)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <input
                id={`upload-${doc.label}`}
                type='file'
                accept='.pdf,.jpg,.jpeg,.png'
                style={{display: 'none'}}
                onChange={(e) => handleFileChange(doc.label, e)}
                disabled={isInteractionDisabled()}
                aria-label={`Upload nih ${doc.label}`}
                multiple
              />
            </div>
          )
        })}

        <style>
          {`
            .documents-container {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 16px;
              margin: 0 auto;
              padding: 12px;
            }

            .document-item {
              background: white;
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
              overflow: hidden;
              border: 1px solid #e9ecef;
              display: flex;
              flex-direction: column;
              height: 100%;
              transition: all 0.2s ease;
            }
            
            .document-item.disabled {
              background: #f8f9fa;
              opacity: 0.75;
              cursor: not-allowed;
            }

            /* Responsive adjustments */
            @media (max-width: 992px) {
              .documents-container {
                grid-template-columns: repeat(2, 1fr);
              }
            }

            @media (max-width: 576px) {
              .documents-container {
                grid-template-columns: 1fr;
              }
            }

            /* Keep the rest of your existing styles */
            .document-header {
              padding: 10px 12px;
              background: #ffffff;
              border-bottom: 1px solid #f1f3f5;
              cursor: pointer;
            }

            .document-header.disabled {
              background: #f8f9fa;
              cursor: not-allowed;
            }

            .document-header-content {
              display: flex;
              align-items: center;
              gap: 10px;
              width: 100%;
              padding: 4px;
            }

            .document-text {
              font-weight: 500;
              color: #2c3e50;
              font-size: 13px;
              flex: 1;
            }

            .document-icon {
              font-size: 16px;
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 6px;
            }

            .document-icon.success {
              color: #20C997;
              background: rgba(32, 201, 151, 0.1);
            }

            .document-icon.warning {
              color: #FD7E14;
              background: rgba(253, 126, 20, 0.1);
            }

            .document-icon.request {
              color: #FA5252;
              background: rgba(250, 82, 82, 0.1);
            }

            .document-files-list {
              padding: 8px 12px;
              background: white;
            }

            .document-file-item {
              display: flex;
              align-items: center;
              padding: 8px;
              border-radius: 4px;
              position: relative;
              cursor: pointer;
            }

            .document-file-item:hover {
              background-color: #f5f5f5;
            }

            .options-modal {
              position: fixed;
              background: white;
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              z-index: 9999;
              min-width: 120px;
              padding: 4px 0;
            }

            .options-modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              z-index: 9998;
            }

            .modal-button {
              display: block;
              width: 100%;
              padding: 8px 16px;
              border: none;
              background: none;
              text-align: left;
              cursor: pointer;
              font-size: 14px;
              color: #333;
            }

            .modal-button:hover {
              background: #f5f5f5;
            }

            .modal-button.delete {
              color: #dc3545;
            }

            .modal-button.delete:hover {
              background: #fff1f1;
            }

            .status-badge {
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 500;
              display: inline-flex;
              align-items: center;
              gap: 4px;
              min-width: 70px;
              justify-content: center;
              background: #f8f9fa;
            }

            .status-badge.valid {
              background: rgba(32, 201, 151, 0.1);
              color: #20C997;
            }

            .status-badge.request {
              background: rgba(250, 82, 82, 0.1);
              color: #FA5252;
            }

            .status-badge.idle {
              background: rgba(253, 126, 20, 0.1);
              color: #FD7E14;
            }

            .document-status {
              margin-left: auto;
              padding: 4px 10px;
              border-radius: 16px;
              font-size: 12px;
              font-weight: 500;
              background: rgba(250, 82, 82, 0.1);
              color: #FA5252;
            }

            @media (max-width: 768px) {
              .documents-container {
                padding: 8px;
              }

              .document-header,
              .document-files-list {
                padding: 8px;
              }

              .document-file-item {
                padding: 8px;
                gap: 8px;
              }

              .status-badge {
                padding: 2px 6px;
                font-size: 10px;
                min-width: 60px;
              }

              .button-icon {
                width: 24px;
                height: 24px;
                font-size: 12px;
              }
              
              .button-icon-wrapper {
                gap: 6px;
                margin-left: 6px;
              }
            }

            .reupload-confirm-modal {
              position: fixed;
              // background: white;
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              z-index: 9999;
              min-width: 200px;
              padding: 12px;
            }

            .reupload-confirm-content {
              display: flex;
              flex-direction: column;
              gap: 12px;
            }

            .reupload-confirm-content p {
              margin: 0;
              font-size: 14px;
              color: #4a5568;
            }

            .reupload-confirm-actions {
              display: flex;
              justify-content: flex-end;
              gap: 8px;
            }

            .modal-button {
              padding: 6px 12px;
              border-radius: 4px;
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
              border: none;
              transition: all 0.2s;
            }

            .modal-button.cancel {
              background: #e9ecef;
              color: #495057;
            }

            .modal-button.cancel:hover {
              background: #dee2e6;
            }

            .modal-button.confirm {
              background: #228be6;
              color: white;
            }

            .modal-button.confirm:hover {
              background: #1c7ed6;
            }

            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: transparent;
              z-index: 9998;
            }

            .upload-button {
              display: flex;
              align-items: center;
              gap: 6px;
              padding: 6px 12px;
              border-radius: 4px;
              background: #f8f9fa;
              color: #495057;
              border: 1px solid #dee2e6;
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
              margin-left: auto;
            }

            .upload-button:hover {
              background: #e9ecef;
              border-color: #ced4da;
            }

            .upload-button.disabled {
              background: #e9ecef;
              color: #adb5bd;
              cursor: not-allowed;
              border-color: #dee2e6;
            }
            
            .upload-button.disabled:hover {
              background: #e9ecef;
            }

            .upload-icon {
              font-size: 14px;
              color: #495057;
            }
          `}
        </style>

        {/* Reupload Confirmation Modal */}
        {/* <Modal
          show={showReuploadModal && reuploadModal.isOpen}
          onHide={closeReuploadModal}
          centered
          size='sm'
          backdrop='static'
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className='d-flex align-items-center'>
                <FaExclamationTriangle className='text-warning me-2' style={{fontSize: '18px'}} />
                Confirm Re-upload
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className='mb-3'>
              Are you sure you want to re-upload the document
              <span className='fw-bold d-block my-1'>{reuploadModal.documentLabel}</span>
              This action will replace the existing file.
            </p>

            <div className='d-flex justify-content-end gap-2'>
              <button className='btn btn-light' onClick={closeReuploadModal}>
                Cancel
              </button>
              <button className='btn btn-primary' onClick={handleConfirmReupload}>
                Re-upload
              </button>
            </div>
          </Modal.Body>
        </Modal> */}

        <ChatModal
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          message={chatMessage}
          claimDetails={claimDetails}
        />

        {/* Centralized Options Modal - similar to DocumentsFGL.tsx approach */}
        <Modal
          show={showOptionsModal && !!activeOptionsModal}
          onHide={closeOptionsModal}
          centered
          size='lg'
          backdrop='static'
          className='document-options-modal'
        >
          <Modal.Header closeButton>
            <Modal.Title>{activeOptionsModal?.label}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='d-flex flex-column'>
              <button
                className='btn btn-light-primary mb-3 d-flex align-items-center'
                // onClick={(e) => {
                //   if (activeOptionsModal) {
                //     handleReUpload(activeOptionsModal.key, 0, e)
                //   }
                // }}
                onClick={handleReUpload}
              >
                <FaCloudUploadAlt className='me-2' />
                {activeOptionsModal?.status === 'IDLE' ? 'Upload' : 'Re-upload'}
              </button>
              <button
                className='btn btn-light-danger d-flex align-items-center'
                onClick={() => {
                  if (activeOptionsModal) {
                    handleDeleteFile(activeOptionsModal.key, 0)
                    closeOptionsModal()
                  }
                }}
                style={{
                  cursor: activeOptionsModal?.status === 'IDLE' ? 'not-allowed' : 'pointer',
                  backgroundColor: activeOptionsModal?.status === 'IDLE' ? '#e9ecef' : '',
                  color: activeOptionsModal?.status === 'IDLE' ? '#adb5bd' : '',
                  borderColor: activeOptionsModal?.status === 'IDLE' ? '#dee2e6' : '',
                  opacity: activeOptionsModal?.status === 'IDLE' ? '0.65' : '1',
                }}
                disabled={activeOptionsModal?.status === 'IDLE'}
              >
                <FaTrash className='me-2' />
                Delete
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  )
}

export default Documents
